import React from 'react'
import {useTranslation} from 'react-i18next'
import PropTypes from 'prop-types'
import {Avatar, Box, Text, Flex, Button} from '@chakra-ui/react'
import {WarningIcon} from '@chakra-ui/icons'
import {ArrowRightIcon, CheckIcon} from '../../icons'

// Statuses where the step is waiting on an external result (a signature, a
// review) rather than on the user unlocking it. They all share the one
// "In progress..." treatment and must not be dimmed like locked steps.
export const PENDING_STATUSES = ['reviewing', 'emailed']

export const CallToAction = ({status, action, ...chakraProps}) => {
  const {t} = useTranslation()

  if (status === 'completed') {
    return (
      <Avatar
        {...chakraProps}
        w="38px"
        h="38px"
        bg="mld.primary.200"
        size="sm"
        color="mld.neutral.0"
        aria-label={t('VerticalStepper.callToAction.completed')}
        icon={<CheckIcon boxSize="25px" />}
      />
    )
  }

  if (PENDING_STATUSES.includes(status)) {
    return (
      <Button
        {...chakraProps}
        variant="primary"
        isLoading
        loadingText={t('VerticalStepper.callToAction.inProgress')}
        aria-label={t('VerticalStepper.callToAction.inProgress')}
        sx={{
          // Chakra dims disabled buttons to 0.4 opacity, which left this
          // label illegible. Full opacity with neutral.800 text keeps the
          // label at WCAG AA contrast on the neutral.200 pill.
          '&:disabled': {
            opacity: 1,
            color: 'mld.neutral.800',
          },
        }}
      />
    )
  }

  if (status === 'declined') {
    return (
      <Flex
        {...chakraProps}
        align="center"
        aria-label={t('VerticalStepper.callToAction.declined')}
      >
        <WarningIcon boxSize="12px" mr="xxs" />
        <Text textStyle="body-small">
          {t('VerticalStepper.callToAction.declined')}
        </Text>
      </Flex>
    )
  }

  if (action) {
    return (
      <Button
        {...chakraProps}
        variant="primary"
        colorScheme="primary"
        isDisabled={action.isDisabled}
        onClick={action.onClick}
        rightIcon={<ArrowRightIcon />}
        sx={{
          // We are already applying opacity: 0.4 to the step itself
          '&:disabled': {
            opacity: 1,
          },
        }}
      >
        {action.label}
      </Button>
    )
  }

  return <Box />
}

CallToAction.propTypes = {
  status: PropTypes.string.isRequired,
  action: PropTypes.PropTypes.shape({
    label: PropTypes.string.isRequired,
    onClick: PropTypes.func.isRequired,
    isDisabled: PropTypes.bool.isRequired,
  }),
}

CallToAction.defaultProps = {
  action: null,
}
